import { makeId } from "shared/imports/sharedHelperFunctions"
import { Set, fromJS, List } from "immutable"
import * as Sentry from "@sentry/browser"
import { unwrapForSearchifyIfNeeded } from "shared/search/helperFunctions"
import { ALL_YEARS } from "shared/profiles/tracking_dashboard/constants"
import { stanceWithCustomLabelByValue } from "shared/stance/helperFunctions"

const { BoardType } = DjangIO.app.tracking_dashboards.types

/**
 * This is a factory function for generating a search term object
 * @name someName
 * @function
 * @returns {Object} - an object with query and displayName keys
 */
// export const generateSearchTerm = () => ({
//     query: "stub",
//     displayName: "stub"
// })
// For now, keep search terms as strings
// We may want to use an object in the near future to allow for a display name
export const generateSearchTerm = () => ""

/**
 * This is a factory function for generating a board
 * @name someName
 * @function
 * This function accepts 1 object as an argument
 * @param {Number} boardType - a member in the BoardType enum
 * Additional args that can be added over time
 * @returns {Object} - A representation of a board
 */
export const generateBoard = ({ boardType, ...args }) => {
    const slug = makeId(20)
    switch (boardType) {
        case BoardType.search_term.value:
            return {
                board_type: boardType,
                name: args.defaultName,
                search_terms: [generateSearchTerm()], // need an element to render at least one search term input
                show_carryover: false,
                show_reviewed: false,
                show_only_no_stance: false,
                slug,
            }
        default:
            return {
                slug,
                board_type: boardType,
                show_carryover: false,
            }
    }
}

/**
 * This is a factory function for generating an empty Saved Search stub for the form
 * @name generateSavedSearchStub
 * @function
 * @returns {Object} - an object added_ids, count, deleted_ids, filters supplied
 */
export const generateSavedSearchStub = () => ({
    added_ids: [],
    count: 0,
    deleted_ids: [],
    filters: [],
})

/**
 * Find the value that is present in one and list and not the other
 * @name findDifferenceElement
 * @function
 * @param {Immutable.Set|Immutable.list} firstList - a List or set of Integer values
 * @param {Immutable.Set|Immutable.list} secondList - a List or set of Integer values
 * @returns {Number} - the number that is present in one list but not the other
 * This function is useful to solve the problem of two lists that only deviate by one value
 */
export const findDifferenceElement = (firstList, secondList) => {
    const firstSet = Set.isSet(firstList) ? firstList : Set(firstList)
    const secondSet = Set.isSet(secondList) ? secondList : Set(secondList)
    if (firstSet.size > secondSet.size) {
        return firstSet.subtract(secondSet).first()
    } else {
        return secondSet.subtract(firstSet).first()
    }
}

/**
 * Construct an Immutable Map, indexed on the slug of each board, from a List of boards
 * @name someName
 * @function
 * @param {List[Map]} boardList - a List of board Maps
 * @returns {Map[Map]} - an Immutable Map of board Maps
 */
export const rebuildMapFromList = (boardList) =>
    boardList.reduce((acc, board) => acc.set(board.get("slug"), board), fromJS({}))

// TODO write a helper function for getting board type, decide in prepareConfiguration if we should merge in and
// remove exiting search boards if shouldShowSearchTermBOards is false

/**
 * Given a configuration and a requests boardType, return a filtered Map of Boards that are of type
 * boardType
 * @name getBoardsOfType
 * @function
 * Function takes 1 object of arguments of type:
 * @param {Map} configuration - a Tracking Dashboard configuration
 * @param {Number} boardType - a enum integer of type BoardType
 * @returns {Map[Map]} - Map of Boards Maps
 */
export const getBoardsOfType = ({ configuration, boardType }) => {
    if (!configuration) {
        return fromJS({})
    }
    return configuration.get("boards", fromJS({})).filter((board) => board.get("board_type") === boardType)
}

/**
 * Given a configuration and a requests boardType, return a filtered Map of Boards
 * without boards of type boardType
 * @name getBoardsExcludingType
 * @function
 * Function takes 1 object of arguments of type:
 * @param {Map} configuration - a Tracking Dashboard configuration
 * @param {Number} boardType - a enum integer of type BoardType to exclude
 * @returns {Map[Map]} - Map of Boards Maps
 */
export const getBoardsExcludingType = ({ configuration, boardType }) => {
    if (!configuration) {
        return fromJS({})
    }
    return configuration.get("boards", fromJS({})).filter((board) => board.get("board_type") !== boardType)
}

/**
 * Get Search term boards for the searchTermBoards redux form field
 * This field is only used to manage Search Term Boards in the form. Upon submission,
 * it gets merged into the configuration
 * @name someName
 * @function
 * @param {Type} argument - a description
 * @returns {List} - an Immutable List of search term boards
 */
export const getSearchTermBoards = ({ configuration = fromJS({}) } = {}) => {
    // when editing, we need to make sure we load existing Search Term Boards
    // if there are none, we want the behavior to be exactly the same for creation and edit
    const searchTermBoards = getBoardsOfType({
        configuration,
        boardType: BoardType.search_term.value,
    }).toList()
    if (searchTermBoards.size) {
        return searchTermBoards
    } else {
        return fromJS([generateBoard({ boardType: BoardType.search_term.value })])
    }
}

/**
 * This function sets a field on each of the immutable Maps passed in the board List
 * @name setFieldOnEachBoard
 * @function
 * @param {List} searchTermBoards - a description
 * @param {String} - field - a field to set
 * @param {Any} - fieldValue - a value
 * @returns {List} - an Immutable List of map boards
 */
export const setFieldOnEachBoard = ({ boards = fromJS([]), field, fieldValue }) =>
    boards.map((board) => board.set(field, fieldValue))

/**
 * Modify the configuration to include additional fields from the form
 * @name prepareConfiguration
 * @function
 * This function takes 1 object of arguments
 * @param {Map} formValues - All of the form values
 * @param {Boolean} shouldShowSearchTermFormSection - whether or not the Search Term option is selected in the Boards step of the form
 * @returns {Map} - A configuration Map that handles setting search term boards and settings
 * Notes:
 *      - this function pulls from search_term_show_only_no_stance and search_term_show_carryover
 */
export const prepareConfiguration = ({ formValues, shouldShowSearchTermFormSection }) => {
    const configuration = formValues.get("configuration")
    const filteredBoards = getBoardsExcludingType({ configuration, boardType: BoardType.search_term.value })
    const filteredBoardsWithCarryover = setFieldOnEachBoard({
        boards: filteredBoards,
        field: "show_carryover",
        fieldValue: formValues.get("searchTermShouldShowCarryover"),
    })

    // if the user wants a search term board(s), merge the separetly managed search term boards into the config
    // we need this Boolean because the form keeps 1 Search Term Board in the form as an initial value
    if (shouldShowSearchTermFormSection) {
        let searchTermBoardList = setFieldOnEachBoard({
            boards: formValues.get("searchTermBoards"),
            field: "show_carryover",
            fieldValue: formValues.get("searchTermShouldShowCarryover"),
        })
        // we want to set selection_criteria and show_only_no stance to values from form every time the form is submitted
        // because we don't want to lose the previous values stored in the configuration
        const immutableSearchTermSelectionCriteria = formValues.get("searchTermSelectionCriteria") || List([])
        searchTermBoardList = setFieldOnEachBoard({
            boards: searchTermBoardList,
            field: "selection_criteria",
            fieldValue: immutableSearchTermSelectionCriteria.toJS(),
        })
        searchTermBoardList = setFieldOnEachBoard({
            boards: searchTermBoardList,
            field: "show_only_no_stance",
            fieldValue: formValues.get("searchTermShouldShowOnlyNoStance"),
        })
        searchTermBoardList = setFieldOnEachBoard({
            boards: searchTermBoardList,
            field: "is_using_custom_selection",
            fieldValue: formValues.get("searchTermCustomSelection"),
        })
        searchTermBoardList = setFieldOnEachBoard({
            boards: searchTermBoardList,
            field: "reviewed_custom_selection_safed_search",
            fieldValue: formValues.get("reviewedSafedSearch"),
        })
        searchTermBoardList = setFieldOnEachBoard({
            boards: searchTermBoardList,
            field: "reviewed_criterion",
            fieldValue: formValues.get("searchTermReviewOption"),
        })
        searchTermBoardList = setFieldOnEachBoard({
            boards: searchTermBoardList,
            field: "reviewed_teams",
            fieldValue: formValues.get("reviewedTeam") && [formValues.get("reviewedTeam")],
        })

        // if we only have 1 Search Term Board, make sure we set the name to Search Terms
        if (searchTermBoardList.size === 1) {
            // create a new List with the search term board
            searchTermBoardList = fromJS([searchTermBoardList.first().set("name", "Search Terms")])
        }
        const searchTermBoards = rebuildMapFromList(searchTermBoardList)

        // we need to filter out the existing boards and update with the new Boards in the form, which may be greater or fewer
        // than what exist in the configuration
        const newAllBoards = filteredBoardsWithCarryover.merge(searchTermBoards)
        return configuration.set("boards", newAllBoards)
    }
    // in this case, we need to make sure we exclude all Search Term Boards from the new configuration to save
    return configuration.set("boards", filteredBoardsWithCarryover)
}

/**
 * Handles setting the value of the year to send to the db:
 * - If the year field has ALl Years selected, we want to send it as null.
 * - If the the year field should should not be shown, we also want to send null as the value
 * - Otherwise, the tb form explicitely states a year and we want to send this value
 *
 * @param {Number} year - the year selected for the TB
 * @param {Boolean} shouldShowYearField (see selectors for logic)
 */
export const getYearValueToSend = (year, shouldShowYearField) => {
    if (year === ALL_YEARS || !shouldShowYearField) {
        return { year: null }
    } else {
        return { year }
    }
}

/**
 * This function converts the Tracking Dashboard form values to an object to submit
 * @name prepareObjectForSubmit
 * @function
 * @param {Object} formValues - formValues from redux-form
 * @returns {Object} - An object to submit
 */
export const prepareObjectForSubmit = ({
    formValues,
    shouldShowIssueFormSection,
    shouldShowSearchTermFormSection,
    shouldUseCustomFilters,
    userId,
    ...additionalArgs
}) => {
    const configuration = prepareConfiguration({
        formValues,
        shouldShowSearchTermFormSection,
    }).toJS()
    const {
        data_type,
        global_saved_search,
        issue_saved_search,
        limited_to_teams,
        is_limited_to_creator,
        is_locked_for_editing,
        name,
        year,
    } = formValues.toJS()

    return {
        // we only want to send the year if Year Field should be shown - see selectors for logic
        // the year field and custom selection via global_saved_search are mutually exclusive
        ...getYearValueToSend(year, additionalArgs.shouldShowYearField),
        ...(shouldUseCustomFilters ? { global_saved_search } : { global_saved_search: null }),
        limited_to_teams,
        is_limited_to_creator,
        configuration,
        data_type,
        is_locked_for_editing,
        name,
        // if Issue is unselected, need to send null to delete the FK for issue_saved_search
        ...(shouldShowIssueFormSection ? { issue_saved_search } : { issue_saved_search: null }),
    }
}

/**
 * This function constructs the name of Issue Lists
 * If necessary, we can have this function take in the board dataType to be more flexible in the future
 * @name getIssueListName
 * @function
 * @param {Number} dataType - An integer representing the Data this Tracking Dashboard is tracking
 * @param {String} listname - the listname, unpadded
 * @param {Boolean} issuesHaveValidParentChildRelationship - Whether the issues selected
 * will result in a dropdown rendering in the Dashboared Profile. See selectors for more info
 * @param {Number} parentId - Parent issues will nave no parentId, therefore this indicates
 * that this issue is a child if it has a parentId
 * @returns {String} - A potentially padded list name
 * TODO test
 */
export const getIssueListName = ({
    dataType, // Bills, Regulations, etc
    isRenderingPrimaryIssuesTab,
    issuesHaveValidParentChildRelationship,
    listName,
    parentId,
}) => {
    const isParentIssue = !parentId // for clarity
    if (issuesHaveValidParentChildRelationship && isParentIssue && !isRenderingPrimaryIssuesTab) {
        return `All ${listName} ${DjangIO.app.models.QuorumDataType.by_value(dataType).plural}`
    } else {
        return listName
    }
}

/**
 * Helper function used in buildSearchFiltersForCustomSelection if is_unreviewed_by_criteria is passed exactly one value
 * This gives an equivalent querymethod which is also a visible search filter so segueing to search applies the expected filters
 * @name getSingleSelectionCriterionFilter
 * @function
 * @param {Array} selectionCriteria - array of SearchTermBoardSelectionCriteriaType values
 * should have length of exactly one based on check in buildSearchFiltersForCustomSelection
 * @returns {Object} - object mapping querymethod to input value for filtering items to search
 */
export const getSingleSelectionCriterionFilter = (selectionCriteria) => {
    const { SearchTermBoardSelectionCriteriaType } = DjangIO.app.tracking_dashboards.types
    switch (selectionCriteria[0]) {
        case SearchTermBoardSelectionCriteriaType.stance.value:
            return { my_stance: DjangIO.app.userdata.types.LegislativeStanceType.no_stance.value }
        case SearchTermBoardSelectionCriteriaType.priority.value:
            return { my_priority: DjangIO.app.userdata.types.LegislativePriorityType.no_priority.value }
        case SearchTermBoardSelectionCriteriaType.issue.value:
            // saved search representing all issues so we can exclude all items with at least 1 issue
            return {
                not_in_issue_or: {
                    addedIds: [],
                    deletedIds: [],
                    querysetList: [{ archived: false }],
                },
            }
        case SearchTermBoardSelectionCriteriaType.assignment.value:
            return { is_assigned: false }
        default:
            return {}
    }
}

/**
 * Generate an object of filters for search, unpacking the for_searchify_filters querymethod and adding
 * any additional required filters
 * @name buildSearchFiltersForCustomSelection
 * @function
 * @param {Object} customSelectionFilters - filters that may have come from searchify, potentially wrapped
 * in for_searchify_filters
 * @param {Object} additionalFilters - an object of additional filters to be applied
 * @returns {Object} - An serializable object of key/value pairs that can be passed via URL params
 * to search
 */
export const buildSearchFiltersForCustomSelection = ({
    customSelectionFilters,
    additionalFilters,
    filtersForSearchMergeObject,
} = {}) => {
    let maybeCustomSelectionFiltersForSearch = {}
    let updatedAdditionalFilters = additionalFilters
    try {
        if (
            (customSelectionFilters && Object.keys(customSelectionFilters).length) ||
            (additionalFilters && Object.keys(additionalFilters).length)
        ) {
            if (
                additionalFilters &&
                additionalFilters.is_unreviewed_by_criteria &&
                additionalFilters.is_unreviewed_by_criteria.length === 1
            ) {
                updatedAdditionalFilters = {
                    ...additionalFilters,
                    ...getSingleSelectionCriterionFilter(additionalFilters.is_unreviewed_by_criteria),
                }
                delete updatedAdditionalFilters.is_unreviewed_by_criteria
            }
            maybeCustomSelectionFiltersForSearch = {
                filtersForSearch: {
                    ...(customSelectionFilters ? unwrapForSearchifyIfNeeded(customSelectionFilters)[0] : {}),
                    ...updatedAdditionalFilters,
                },
            }
        }
        if (filtersForSearchMergeObject) {
            maybeCustomSelectionFiltersForSearch.filtersForSearch = {
                ...maybeCustomSelectionFiltersForSearch.filtersForSearch,
                ...(filtersForSearchMergeObject ? filtersForSearchMergeObject : {}),
            }
        }
    } catch (error) {
        Sentry.captureException(error)
        console.warn("Unable to build search filters properly")
    }
    return maybeCustomSelectionFiltersForSearch
}

/**
 * Update the board name to use stance custom labels if there is a unique stance custom label
 * @name updateBoardNameWithStanceCustomLabel
 * @function
 * @param {String} originalBoardName - original display name for board
 * @param {Boolean} wordToReplace - word to replace with stance custom label
 * @param {Object} stanceOriginalLabel - label of stance stored on LegislativeStanceType enum
 * @param {Boolean} stanceCustomLabel - custom label of stance for user
 * @returns {String} - name of the board to show in tab
 */
export const updateBoardNameWithStanceCustomLabel = ({
    originalBoardName,
    wordToReplace,
    stanceOriginalLabel,
    stanceCustomLabel,
}) => {
    if (stanceOriginalLabel !== stanceCustomLabel) {
        return originalBoardName.replace(wordToReplace, stanceCustomLabel)
    }
    return originalBoardName
}

export const getAllItemsListName = ({ dataType, boardType, stancesWithCustomLabels }) => {
    const dataTypeName = DjangIO.app.models.QuorumDataType.by_value(dataType).plural
    switch (boardType) {
        case BoardType.status_with_issues.value:
        case BoardType.region_with_issues.value:
            return `All ${dataTypeName} with an Issue`
        case BoardType.region_with_stances.value:
        case BoardType.status_with_stances.value:
            return `All ${dataTypeName} with a Stance`
        case BoardType.supported_by_status.value:
            return updateBoardNameWithStanceCustomLabel({
                originalBoardName: `All Supported ${dataTypeName}`,
                wordToReplace: "Supported",
                stanceOriginalLabel: DjangIO.app.userdata.types.LegislativeStanceType.support.label,
                stanceCustomLabel: stanceWithCustomLabelByValue(
                    stancesWithCustomLabels,
                    DjangIO.app.userdata.types.LegislativeStanceType.support.value,
                ).label,
            })
        case BoardType.opposed_by_status.value:
            return updateBoardNameWithStanceCustomLabel({
                originalBoardName: `All Opposed ${dataTypeName}`,
                wordToReplace: "Opposed",
                stanceOriginalLabel: DjangIO.app.userdata.types.LegislativeStanceType.oppose.label,
                stanceCustomLabel: stanceWithCustomLabelByValue(
                    stancesWithCustomLabels,
                    DjangIO.app.userdata.types.LegislativeStanceType.oppose.value,
                ).label,
            })
        case BoardType.high_priority_by_status.value:
            return `All High Priority ${dataTypeName}`
        default:
            return `No Name`
    }
}

export const prepareAllItemsListProps = ({
    additionalFilters,
    boardSlug,
    boardType,
    dataType,
    filterKey,
    globalFilters,
    regionTypes = [],
    statusTypes,
    stancesWithCustomLabels,
}) => {
    switch (boardType) {
        case BoardType.region_with_stances.value:
        case BoardType.region_with_issues.value:
            return [
                {
                    dataType,
                    id: `${boardSlug}-all`,
                    ...buildSearchFiltersForCustomSelection({
                        customSelectionFilters: globalFilters,
                        additionalFilters: {
                            ...additionalFilters,
                            [filterKey]: regionTypes.map((r) => r.value),
                        },
                    }),
                    name: getAllItemsListName({ dataType, boardType, stancesWithCustomLabels }),
                    permanentFilters: {
                        ...globalFilters,
                        [filterKey]:
                            dataType === DjangIO.app.models.QuorumDataType.bill.value
                                ? regionTypes.map((r) => r.value)
                                : {
                                      addedIds: regionTypes.map((r) => r.value),
                                      deletedIds: [],
                                      querysetList: [],
                                  },
                        ...additionalFilters,
                    },
                },
            ]

        case BoardType.status_with_stances.value:
        case BoardType.status_with_issues.value:
        case BoardType.supported_by_status.value:
        case BoardType.opposed_by_status.value:
        case BoardType.high_priority_by_status.value:
            return [
                {
                    dataType,
                    id: `${boardSlug}-all`,
                    ...buildSearchFiltersForCustomSelection({
                        customSelectionFilters: globalFilters,
                        additionalFilters: {
                            ...additionalFilters,
                            [filterKey]: statusTypes.map((s) => s.value).join(","),
                        },
                    }),
                    name: getAllItemsListName({
                        dataType,
                        boardType,
                        stancesWithCustomLabels,
                    }),
                    permanentFilters: {
                        ...globalFilters,
                        [filterKey]: statusTypes.map((s) => s.value).join(","),
                        ...additionalFilters,
                    },
                },
            ]
        default:
            return []
    }
}

/**
 * Given a set of inputs, return an object with key/value pairs corresponding to the props
 * Required for a list to render appropriately
 * @name prepareObjectForListsContainer
 * @function
 * @param {Object} additionalFilters - additional filters to filter the search endpoint by
 * @param {String} boardSlug - a unique indentifier for the board
 * @param {Number} boardType - A integer in the BoardType enum
 * @param {Number} dataType - A integer in the QuorumDataType enum, representing the type of Tracking Dashboard
 * @param {String} filterKey - An additional filter/querymethod to apply to a particular Board, such as my_stance
 *
 * @returns {Type} - Something
 */
export const prepareObjectForListsContainer = ({
    additionalFilters = {},
    boardSlug,
    boardType,
    dataType,
    filterKey,
    globalFilters,
    isRenderingPrimaryIssuesTab,
    issuesHaveValidParentChildRelationship,
    listStubObj,
    ...args
}) => {
    switch (boardType) {
        case BoardType.assignment.value:
            return {
                dataType,
                id: `${boardSlug}-${listStubObj.id}-${listStubObj.first_name}-${listStubObj.last_name}`,
                name: `Assigned to ${listStubObj.first_name} ${listStubObj.last_name}`,
                permanentFilters: {
                    ...globalFilters,
                    assigned_to: { addedIds: [listStubObj.id] },
                },
            }
        case BoardType.region_with_issues.value:
        case BoardType.region_with_priorities.value:
        case BoardType.region_with_stances.value:
            const filter =
                dataType === DjangIO.app.models.QuorumDataType.bill.value
                    ? listStubObj.value
                    : {
                          addedIds: [listStubObj.database_id],
                          deletedIds: [],
                          querysetList: [],
                      }
            return {
                dataType,
                id: `${boardSlug}-${listStubObj.label}`,
                ...buildSearchFiltersForCustomSelection({
                    customSelectionFilters: globalFilters,
                    additionalFilters: {
                        ...additionalFilters,
                        [filterKey]: filter,
                    },
                }),
                name: listStubObj.region_name,
                permanentFilters: {
                    ...globalFilters,
                    [filterKey]: filter,
                    ...additionalFilters,
                },
            }
        case BoardType.status_with_issues.value:
        case BoardType.status_with_priorities.value:
        case BoardType.status_with_stances.value:
        case BoardType.supported_by_status.value:
        case BoardType.opposed_by_status.value:
        case BoardType.high_priority_by_status.value:
            return {
                dataType,
                id: `${boardSlug}-${listStubObj.label}`,
                ...buildSearchFiltersForCustomSelection({
                    customSelectionFilters: globalFilters,
                    additionalFilters: {
                        ...additionalFilters,
                        [filterKey]: listStubObj.value,
                    },
                }),
                name: listStubObj.label,
                permanentFilters: {
                    ...globalFilters,
                    [filterKey]: listStubObj.value,
                    ...additionalFilters,
                },
            }
        case BoardType.priority.value:
        case BoardType.stance.value:
            return {
                dataType,
                id: `${boardSlug}-${listStubObj.label}`,
                ...buildSearchFiltersForCustomSelection({
                    customSelectionFilters: globalFilters,
                    additionalFilters: {
                        ...additionalFilters,
                        [filterKey]: listStubObj.value,
                    },
                }),
                name: listStubObj.label,
                permanentFilters: {
                    ...globalFilters,
                    ...additionalFilters,
                    [filterKey]: listStubObj.value,
                },
            }
        case BoardType.search_term.value:
            // allow users to mark bills as unimportant if they are using stances to manage their Boards
            return {
                canRemoveListItems:
                    "is_unreviewed_by_criteria" in additionalFilters &&
                    additionalFilters.is_unreviewed_by_criteria.includes(
                        DjangIO.app.tracking_dashboards.types.SearchTermBoardSelectionCriteriaType.stance.value,
                    ),
                dataType,
                id: `${boardSlug}-${listStubObj.searchTerm}`,
                ...buildSearchFiltersForCustomSelection({
                    customSelectionFilters: globalFilters,
                    additionalFilters: {
                        ...additionalFilters,
                        [filterKey]: listStubObj.searchTerm,
                    },
                    filtersForSearchMergeObject: {
                        filters: {
                            [filterKey]: listStubObj.searchTerm,
                        },
                    },
                }),
                name: listStubObj.searchTerm,
                permanentFilters: {
                    ...globalFilters,
                    // apply filters such as stance, reviewed, carry over legislation
                    ...additionalFilters,
                    [filterKey]: listStubObj.searchTerm,
                },
            }
        case BoardType.issue.value:
            return {
                dataType,
                id: `${boardSlug}-${listStubObj.name}-${listStubObj.id}`,
                ...buildSearchFiltersForCustomSelection({
                    customSelectionFilters: globalFilters,
                    additionalFilters: {
                        [filterKey]: listStubObj.id,
                    },
                }),
                name: getIssueListName({
                    dataType,
                    isRenderingPrimaryIssuesTab,
                    issuesHaveValidParentChildRelationship,
                    listName: listStubObj.name,
                    parentId: listStubObj.parent_id,
                }),
                permanentFilters: {
                    ...globalFilters,
                    [filterKey]: listStubObj.id,
                },
            }
        default:
            console.warn("Need to implement a Stub for this Board type")
            return {
                dataType,
            }
    }
}

/**
 * Get the board name to display in a Tab on the Tracking Dashboard
 * @name getBoardName
 * @function
 * @param {Object} boardConfig - the Board configuration object
 * @param {Boolean} issuesHaveValidParentChildRelationship - whether Issues selected have a parent/child relationship
 * @param {Object} parentIssue - configuration of parentIssue if issuesHaveValidParentChildRelationship
 * @param {Boolean} multipleSearchTermGroups - whether the board has multiple search term boards
 * @param {Array} stancesWithCustomLabels - list of LegislativeStanceTypes with custom labels
 * @returns {String} - name of the board to show in tab
 */
export const getBoardName = ({
    boardConfig,
    issuesHaveValidParentChildRelationship,
    parentIssue = {},
    multipleSearchTermGroups,
    stancesWithCustomLabels,
}) => {
    switch (boardConfig.board_type) {
        case BoardType.priority.value:
        case BoardType.stance.value:
            return BoardType.by_value(boardConfig.board_type).label
        case BoardType.issue.value:
            if (issuesHaveValidParentChildRelationship) {
                // if we were passed a parentIssue, we are rendering one of the parent issues
                // under Primary Issues
                if (parentIssue.name) {
                    return parentIssue.name
                }
                return "All Primary Issues"
            } else {
                return BoardType.by_value(boardConfig.board_type).label
            }
        case BoardType.search_term.value:
            if (!multipleSearchTermGroups) {
                return "Unreviewed"
            }
            return boardConfig.name

        case BoardType.assignment.value:
            return `Assignment`

        case BoardType.supported_by_status.value:
            return updateBoardNameWithStanceCustomLabel({
                originalBoardName: BoardType.by_value(boardConfig.board_type).dashboard_name,
                wordToReplace: "Supported",
                stanceOriginalLabel: DjangIO.app.userdata.types.LegislativeStanceType.support.label,
                stanceCustomLabel: stanceWithCustomLabelByValue(
                    stancesWithCustomLabels,
                    DjangIO.app.userdata.types.LegislativeStanceType.support.value,
                ).label,
            })

        case BoardType.opposed_by_status.value:
            return updateBoardNameWithStanceCustomLabel({
                originalBoardName: BoardType.by_value(boardConfig.board_type).dashboard_name,
                wordToReplace: "Opposed",
                stanceOriginalLabel: DjangIO.app.userdata.types.LegislativeStanceType.oppose.label,
                stanceCustomLabel: stanceWithCustomLabelByValue(
                    stancesWithCustomLabels,
                    DjangIO.app.userdata.types.LegislativeStanceType.oppose.value,
                ).label,
            })

        case BoardType.status_with_issues.value:
        case BoardType.region_with_issues.value:
        case BoardType.status_with_priorities.value:
        case BoardType.region_with_priorities.value:
        case BoardType.status_with_stances.value:
        case BoardType.region_with_stances.value:
        case BoardType.high_priority_by_status.value: {
            // const name = DjangIO.app.models.QuorumDataType.by_value(dataType).plural
            return BoardType.by_value(boardConfig.board_type).dashboard_name
        }
        default:
            return "No Name Yet"
    }
}

/**
 * return an object of the form parentTab: name if a parentTab is needed
 * @name getParentTabKeyValuePair
 * @function
 * This function takes 1 object of arguments
 * @param {boardConfig} Object - a board configuration object
 * @returns {Object|undefined} - Either an object of the form { parentTab: [parentTabNameString]}, or undefined if not needed
 */
export const getParentTabKeyValuePair = ({ boardConfig, issuesHaveValidParentChildRelationship }) => {
    switch (boardConfig.board_type) {
        case BoardType.issue.value:
            // We need all parent issues to be group under Pr
            if (issuesHaveValidParentChildRelationship) {
                return { parentTab: "Issues" }
            } else {
                return undefined
            }
        // Giving a Search Term Tab a parentTab if there is one board does
        // not render a dropdown which is a convenient
        case BoardType.search_term.value:
            return {
                parentTab: "Unreviewed",
            }
        default:
            return undefined
    }
}

/**
 * This function computes the path
 * @name getPathName
 * @function
 * @param {Object} boardConfig - a board configuration object
 * @param {Object} parentIssue - An object representing the parent issue
 *
 * @returns {String} - a partial url path
 */
export const getPathName = ({ boardConfig, parentIssue }) => {
    // Provide the parent id in url params if getting the path name of a Parent Issue
    if (parentIssue.id) {
        return `/${boardConfig.slug}/${parentIssue.id}`
    } else {
        return `/${boardConfig.slug}`
    }
}

/**
 * This function returns an Object for Profiles tab selector
 * @name prepareTabObject
 * @function
 * @param {Object} boardConfig - A board configuration object
 * @param {Number} dataType - The data type of the Tracking Dashboard
 * @param {Boolean} issuesHaveValidParentChildRelationship - whether Issues selected have a parent/child relationship
 * @param {Object} parentIssue - configuration of parentIssue if issuesHaveValidParentChildRelationship
 * @param {Boolean} multipleSearchTermGroups - whether there is greater than 1 search term board for the tracking board
 * @param {Array} stancesWithCustomLabels - list of stances with custom labels to be used in getBoardName
 * @returns {Object} - An object of the form: {
 *
 * }
 */
export const prepareTabObject = ({
    boardConfig,
    dataType,
    issuesHaveValidParentChildRelationship,
    parentIssue = {},
    multipleSearchTermGroups,
    stancesWithCustomLabels,
}) => {
    const baseObject = {
        // needed for sorting tabs
        board_type: boardConfig.board_type,
        board_slug: boardConfig.slug,
        displayName: getBoardName({
            boardConfig,
            dataType,
            issuesHaveValidParentChildRelationship,
            parentIssue,
            multipleSearchTermGroups,
            stancesWithCustomLabels,
        }),
        name: boardConfig.name,
        pathName: getPathName({ boardConfig, parentIssue }),
        ...getParentTabKeyValuePair({ boardConfig, issuesHaveValidParentChildRelationship }),
    }

    return baseObject
}

/**
 * This function sorts Tab objects primarly by their order and secondarily by name
 * @name sortTabs
 * @function
 * @param {Array[Object]} tabArray - an array of tab objects for Profile tab infrastructure
 * @returns {Type} - Array[Object] - the same tabArray sorted
 */
export const sortTabs = (tabArray = []) =>
    tabArray.sort((firstBoard, secondBoard) => {
        try {
            const firstBoardOrder = BoardType.by_value(firstBoard.board_type).order
            const secondBoardOrder = BoardType.by_value(secondBoard.board_type).order
            if (firstBoardOrder === secondBoardOrder) {
                return firstBoard.name.localeCompare(secondBoard.name)
            } else {
                return firstBoardOrder - secondBoardOrder
            }
        } catch (e) {
            console.warn("unable to determine board ordering")
            // if there was an error, default to no sorting
            return 0
        }
    })

/**
 * This function sorts an Immutable Array of board objects
 * @name sortImmutableBoards
 * @function
 * @param {Immutable[List]} boards - an Immutable List of Board objects
 * @returns {{Immutable[List]}
 */
export const sortImmutableBoards = (boards = fromJS([])) =>
    boards.sort((firstBoard, secondBoard) => {
        const firstBoardOrder =
            firstBoard.get("board_type", null) && BoardType.by_value(firstBoard.get("board_type")).order
        const secondBoardOrder =
            firstBoard.get("board_type", null) && BoardType.by_value(secondBoard.get("board_type")).order

        if (firstBoard === secondBoardOrder && false) {
            // turn this off for now
            return firstBoard.name.localeCompare(secondBoard.name)
        } else {
            return firstBoardOrder - secondBoardOrder
        }
    })

/**
 * This function returns the child issues that belong to the parentIssueId passed in
 * @name getChildIssuesForParent
 * @function
 * @param {List} childIssues - an Immutable List of child issue
 * @param {Number} parentIssueId - the database id of the parent issue
 * TODO test
 * @returns {List[Map]} - Immutable List of child issues
 */
export const getChildIssuesForParent = ({ childIssues = fromJS([]), parentIssue }) =>
    childIssues.filter((issue) => issue.get("ancestry_ids", []).includes(parentIssue.get("id")))

/**
 * This function determines if editing should be disabled for the current user
 * @name shouldDisableEditingForUser
 * @function
 * @param {Boolean} isLockedForEditing - Whether the Tracking Dashboard should be locked for editing
 * @param {Boolean} isOrgAdmin - Whether the current user is an org admin
 * @param {Boolean} isCreator - Whether the current user is the creator of this sheet
 * @returns {Boolean} - Wether editing should be disabled for the current user
 */
export const shouldDisableEditingForUser = (isLockedForEditing, isOrgAdmin, isCreator) => {
    if (isOrgAdmin || isCreator) {
        return false
    }
    return Boolean(isLockedForEditing)
}
